import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { SIMULATORS } from "@/config/simulators";
import { parseNum } from "@/lib/format";
import { api, saveSim } from "@/lib/api";
import { trackStep } from "@/lib/traffic";

export default function Simulator() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const config = SIMULATORS[slug];

  const [stepIndex, setStepIndex] = useState(0);
  const [values, setValues] = useState(() => {
    const init = {};
    if (config) {
      config.steps.forEach((s) =>
        s.fields.forEach((f) => {
          init[f.key] = f.default ?? "";
          if (f.period) init[`${f.key}Periode`] = f.periodDefault ?? "mensuelle";
        })
      );
    }
    return init;
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const totalSteps = config ? config.steps.length : 0;
  const step = config ? config.steps[stepIndex] : null;
  const progress = useMemo(
    () => ((stepIndex + 1) / totalSteps) * 100,
    [stepIndex, totalSteps]
  );

  useEffect(() => {
    if (config) trackStep(slug, "arrivee");
  }, [slug, config]);

  if (!config) {
    return (
      <div className="p-6">
        <p className="text-[#231C18]">Simulateur introuvable.</p>
        <button onClick={() => navigate("/")} className="mt-4 underline text-[#C84C2C]">
          Retour à l'accueil
        </button>
      </div>
    );
  }

  const setField = (key, val) => {
    setValues((v) => ({ ...v, [key]: val }));
    trackStep(slug, "commencee");
    setErrors((e) => ({ ...e, [key]: false }));
  };

  const validateStep = () => {
    const errs = {};
    step.fields.forEach((f) => {
      if (isNaN(parseNum(values[f.key]))) errs[f.key] = true;
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async () => {
    const payload = {};
    config.steps.forEach((s) =>
      s.fields.forEach((f) => {
        payload[f.key] = parseNum(values[f.key]);
        if (f.period) payload[`${f.key}Periode`] = values[`${f.key}Periode`];
      })
    );
    setLoading(true);
    try {
      const { data } = await api.post(config.endpoint, payload);
      saveSim({ slug, inputs: payload, resultat: data });
      trackStep(slug, "terminee");
      navigate("/resultat");
    } catch (e) {
      toast.error("Une erreur est survenue. Vérifiez vos saisies.");
    } finally {
      setLoading(false);
    }
  };

  const next = () => {
    if (!validateStep()) {
      toast.error("Merci de renseigner tous les champs.");
      return;
    }
    trackStep(slug, "commencee");
    if (stepIndex < totalSteps - 1) {
      setStepIndex((i) => i + 1);
      window.scrollTo(0, 0);
    } else {
      submit();
    }
  };

  const back = () => {
    if (stepIndex > 0) setStepIndex((i) => i - 1);
    else navigate("/");
  };

  const isLast = stepIndex === totalSteps - 1;

  return (
    <div className="flex flex-col min-h-screen" data-testid="simulator-screen">
      <div className="sticky top-0 z-30 bg-[#FAF6F0]/90 backdrop-blur-md px-6 pt-5 pb-4">
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={back}
            data-testid="sim-back-btn"
            className="h-9 w-9 -ml-2 rounded-full flex items-center justify-center text-[#8C7A70] hover:bg-[#F5EFE6]"
          >
            <ArrowLeft strokeWidth={1.5} className="h-5 w-5" />
          </button>
          <span className="text-xs font-semibold tracking-wide text-[#8C7A70]">
            Étape {stepIndex + 1} sur {totalSteps}
          </span>
        </div>
        <div className="h-1.5 w-full bg-[#E6DDD4] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#C84C2C] rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div key={stepIndex} className="flex-1 px-6 pt-4 pb-40 animate-fade-up">
        <h2 className="font-serif text-2xl font-semibold text-[#231C18]">
          {step.title}
        </h2>
        <p className="mt-1.5 text-sm text-[#8C7A70]">{step.subtitle}</p>

        <div className="mt-7 space-y-5">
          {step.fields.map((f) => (
            <div key={f.key}>
              <label className="block text-sm font-semibold text-[#231C18] mb-2">
                {f.label}
              </label>
              <div className="relative flex items-center w-full">
                <input
                  data-testid={`sim-input-${f.key}`}
                  inputMode="decimal"
                  value={values[f.key]}
                  onChange={(e) => setField(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className={`h-[56px] w-full bg-white shadow-[0_1px_2px_rgba(35,28,24,0.04)] border rounded-2xl pl-4 ${
                    f.suffix ? "pr-12" : "pr-4"
                  } text-[#231C18] text-lg font-medium tabular-nums outline-none transition-all placeholder-[#8C7A70]/60 ${
                    errors[f.key]
                      ? "border-[#C62828] ring-1 ring-[#C62828]"
                      : "border-[#E6DDD4] focus:border-[#C84C2C] focus:ring-4 focus:ring-[#C84C2C]/15"
                  }`}
                />
                {f.suffix && (
                  <span className="absolute right-4 text-[#8C7A70] font-medium pointer-events-none">
                    {f.suffix}
                  </span>
                )}
              </div>
              {f.period && (
                <div className="mt-2 flex gap-2" data-testid={`sim-period-${f.key}`}>
                  {[["mensuelle", "Mensuel"], ["trimestrielle", "Trimestriel"], ["annuelle", "Annuel"]].map(([val, lab]) => (
                    <button
                      key={val}
                      type="button"
                      data-testid={`sim-period-${f.key}-${val}`}
                      onClick={() => setField(`${f.key}Periode`, val)}
                      className={`flex-1 h-9 rounded-full text-xs font-semibold transition-colors ${
                        values[`${f.key}Periode`] === val
                          ? "bg-[#1C1613] text-[#FFFFFF]"
                          : "bg-[#F5EFE6] text-[#8C7A70] hover:bg-[#E6DDD4]"
                      }`}
                    >
                      {lab}
                    </button>
                  ))}
                </div>
              )}
              {f.help && (
                <p className="mt-1.5 text-xs text-[#8C7A70] leading-snug">{f.help}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="fixed bottom-0 max-w-[480px] w-full px-6 py-6 bg-gradient-to-t from-[#FAF6F0] via-[#FAF6F0] to-transparent z-40">
        <button
          onClick={next}
          disabled={loading}
          data-testid="sim-continue-btn"
          className="h-[56px] w-full bg-[#C84C2C] text-white rounded-full font-semibold shadow-[0_8px_20px_-6px_rgba(200,76,44,0.55)] transition-transform active:scale-[0.98] hover:bg-[#B23E20] disabled:opacity-60"
        >
          {loading ? "Calcul en cours…" : isLast ? config.cta : "Continuer"}
        </button>
      </div>
    </div>
  );
}
